import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Zap, Mail, Send, Check, AlertCircle, ExternalLink, Camera, RefreshCw, Globe, Plus, Trash2, Copy, ChevronDown, ChevronRight } from "lucide-react";

export default function AdminNotifications() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [settings, setSettings] = useState({
    signup_webhook_url: "",
    upgrade_webhook_url: "",
    digest_enabled: false,
    digest_email: "",
    resend_configured: false,
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/notifications/settings");
      setSettings(r.data);
    } catch (e) {
      toast.error("Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/admin/notifications/settings", {
        signup_webhook_url: settings.signup_webhook_url || "",
        upgrade_webhook_url: settings.upgrade_webhook_url || "",
        digest_enabled: !!settings.digest_enabled,
        digest_email: settings.digest_email || "",
      });
      setSettings((prev) => ({ ...prev, ...r.data }));
      toast.success("Notification settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const testWebhook = async (which) => {
    const url = which === "signup" ? settings.signup_webhook_url : settings.upgrade_webhook_url;
    if (!url) { toast.error("Enter a webhook URL first"); return; }
    setTesting(true);
    try {
      const payload = which === "signup" ? { signup_webhook_url: url } : { upgrade_webhook_url: url };
      const r = await api.post("/admin/notifications/test-webhook", payload);
      if (r.data.ok) {
        toast.success(`Pinged ${r.data.url} · ${r.data.status_code}`);
      } else {
        toast.error(`Failed: ${r.data.error || r.data.status_code}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const r = await api.post("/admin/notifications/digest/send-now?hours=24");
      toast.success(`Digest sent to ${r.data.recipient} (${r.data.totals.new_signups} signups, ${r.data.totals.new_upgrades} upgrades)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Digest send failed");
    } finally {
      setSendingDigest(false);
    }
  };

  if (loading) return <div className="text-zinc-500 p-6">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="admin-notifications-panel">
      {/* Webhook section */}
      <section className="border-2 border-black">
        <div className="px-4 py-3 border-b-2 border-black bg-zinc-50 flex items-center gap-2">
          <Zap size={16} className="text-[#FF4F00]" />
          <div className="font-display font-bold text-lg">Outbound webhooks</div>
        </div>
        <div className="p-5 space-y-5">
          <p className="text-sm text-zinc-600 leading-relaxed">
            VibeLead POSTs a JSON payload to your webhook URL every time a customer signs up (free)
            or upgrades (paid). Plug in a <a href="https://zapier.com/apps/webhook/integrations" target="_blank" rel="noreferrer" className="underline font-bold">Zapier catch hook</a> or
            <a href="https://www.make.com/en/integrations/webhooks" target="_blank" rel="noreferrer" className="underline font-bold"> Make scenario</a> to route leads to AWeber, GetResponse, ConvertKit, Slack — anywhere.
          </p>

          <WebhookField
            label="New signup webhook"
            hint="Fires on every free signup (password or Google). Payload: { event: 'user.signup', email, name, plan, source, referred_by }"
            value={settings.signup_webhook_url}
            onChange={(v) => setSettings((p) => ({ ...p, signup_webhook_url: v }))}
            onTest={() => testWebhook("signup")}
            testing={testing}
            testid="signup-webhook"
          />

          <WebhookField
            label="Paid upgrade webhook"
            hint="Fires when Stripe confirms payment. Payload: { event: 'user.upgrade', email, plan (pro_monthly|pro_yearly), amount, currency, session_id }"
            value={settings.upgrade_webhook_url}
            onChange={(v) => setSettings((p) => ({ ...p, upgrade_webhook_url: v }))}
            onTest={() => testWebhook("upgrade")}
            testing={testing}
            testid="upgrade-webhook"
          />
        </div>
      </section>

      {/* Digest section */}
      <section className="border-2 border-black">
        <div className="px-4 py-3 border-b-2 border-black bg-zinc-50 flex items-center gap-2">
          <Mail size={16} className="text-[#FF4F00]" />
          <div className="font-display font-bold text-lg">Daily email digest</div>
          {settings.resend_configured ? (
            <span className="ml-auto flex items-center gap-1 text-xs font-bold px-2 py-1 border-2 border-black bg-green-100" data-testid="resend-status-ok">
              <Check size={12} /> Resend connected
            </span>
          ) : (
            <span className="ml-auto flex items-center gap-1 text-xs font-bold px-2 py-1 border-2 border-black bg-red-100" data-testid="resend-status-missing">
              <AlertCircle size={12} /> Resend key missing
            </span>
          )}
        </div>
        <div className="p-5 space-y-5">
          <p className="text-sm text-zinc-600 leading-relaxed">
            Get a daily summary at <strong>9:00 UTC</strong> with last 24h signups + paid upgrades.
            Backup for the webhooks — runs even if your Zapier scenario is off.
          </p>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="digest-enabled-toggle">
            <input
              type="checkbox"
              checked={!!settings.digest_enabled}
              onChange={(e) => setSettings((p) => ({ ...p, digest_enabled: e.target.checked }))}
              className="mt-1 w-5 h-5 accent-[#FF4F00]"
            />
            <div>
              <div className="font-display font-bold">Enable daily digest</div>
              <div className="text-xs text-zinc-500">Scheduler runs daily at 9:00 UTC.</div>
            </div>
          </label>

          <div>
            <label className="label-cap block mb-2">Recipient email</label>
            <input
              value={settings.digest_email || ""}
              onChange={(e) => setSettings((p) => ({ ...p, digest_email: e.target.value }))}
              className="input-brutal"
              placeholder="you@example.com"
              data-testid="digest-email-input"
            />
            <div className="text-xs text-zinc-500 mt-1">
              Tip: until you verify a domain at <a href="https://resend.com/domains" target="_blank" rel="noreferrer" className="underline">resend.com/domains</a>,
              Resend only delivers to the address that owns the API key.
            </div>
          </div>

          <button
            onClick={sendDigestNow}
            disabled={sendingDigest || !settings.resend_configured}
            className="btn-brutal-outline !px-4 !py-2 text-sm inline-flex items-center gap-2"
            data-testid="send-digest-now-btn"
          >
            <Send size={14} /> {sendingDigest ? "Sending…" : "Send digest now (preview)"}
          </button>
        </div>
      </section>

      <ResendDomainsPanel resendConfigured={settings.resend_configured} />

      <ScreenshotsPanel />

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} className="btn-brutal" data-testid="save-notification-settings-btn">
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </div>
  );
}

function ScreenshotsPanel() {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const loadStatus = async () => {
    try {
      const r = await api.get("/admin/screenshots/status");
      setStatus(r.data);
      return r.data;
    } catch (e) {
      // ignore: status polling is best-effort
      return null;
    }
  };

  useEffect(() => {
    loadStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const beginPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const s = await loadStatus();
      if (s && !s.running) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (s.last_result === "success") {
          toast.success("Screenshots refreshed. Reload the marketing page to see them.");
        } else if (s.last_result === "failed") {
          toast.error(`Refresh failed: ${s.last_error || "see logs"}`);
        }
      }
    }, 3000);
  };

  const startRefresh = async () => {
    setStarting(true);
    try {
      const r = await api.post("/admin/screenshots/refresh");
      toast.message(r.data.message || "Started…");
      await loadStatus();
      beginPolling();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start refresh");
    } finally {
      setStarting(false);
    }
  };

  const running = status?.running;
  const lastResult = status?.last_result;
  const lastRunAt = status?.last_run_at;
  const lastRunDisplay = lastRunAt ? new Date(lastRunAt).toLocaleString() : "never";

  return (
    <section className="border-2 border-black" data-testid="screenshots-panel">
      <div className="px-4 py-3 border-b-2 border-black bg-zinc-50 flex items-center gap-2">
        <Camera size={16} className="text-[#FF4F00]" />
        <div className="font-display font-bold text-lg">Marketing screenshots</div>
        {lastResult === "success" && (
          <span className="ml-auto flex items-center gap-1 text-xs font-bold px-2 py-1 border-2 border-black bg-green-100">
            <Check size={12} /> Last run OK
          </span>
        )}
        {lastResult === "failed" && (
          <span className="ml-auto flex items-center gap-1 text-xs font-bold px-2 py-1 border-2 border-black bg-red-100">
            <AlertCircle size={12} /> Last run failed
          </span>
        )}
      </div>
      <div className="p-5 space-y-4">
        <p className="text-sm text-zinc-600 leading-relaxed">
          Regenerates the 5 product screenshots used in the Landing page "How It Works" section by driving a real browser
          through Templates → Modal → Builder → Public Landing → Delivery. Takes ~30 seconds.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={startRefresh}
            disabled={starting || running}
            className="btn-brutal-outline !px-4 !py-2 text-sm inline-flex items-center gap-2"
            data-testid="refresh-screenshots-btn"
          >
            <RefreshCw size={14} className={running ? "animate-spin" : ""} />
            {running ? "Regenerating…" : starting ? "Starting…" : "Refresh screenshots"}
          </button>
          <div className="text-xs text-zinc-500">Last run: {lastRunDisplay}</div>
        </div>
        {status?.last_log_tail && (
          <details className="text-xs">
            <summary className="cursor-pointer text-zinc-600">View last log</summary>
            <pre className="mt-2 p-3 bg-zinc-50 border-2 border-black overflow-auto max-h-48 whitespace-pre-wrap" data-testid="screenshots-log">{status.last_log_tail}</pre>
          </details>
        )}
      </div>
    </section>
  );
}

function WebhookField({ label, hint, value, onChange, onTest, testing, testid }) {
  return (
    <div>
      <label className="label-cap block mb-2">{label}</label>
      <div className="flex gap-2">
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="input-brutal flex-1"
          placeholder="https://hooks.zapier.com/hooks/catch/..."
          data-testid={`${testid}-input`}
        />
        <button
          onClick={onTest}
          disabled={testing || !value}
          className="btn-brutal-outline !px-3 !py-2 text-sm inline-flex items-center gap-1 whitespace-nowrap"
          data-testid={`${testid}-test-btn`}
        >
          <ExternalLink size={14} /> {testing ? "…" : "Test"}
        </button>
      </div>
      <div className="text-xs text-zinc-500 mt-1">{hint}</div>
    </div>
  );
}


// ===========================================================================
// Resend Domains Panel — verify a sending domain so leads' inboxes get mail
// ===========================================================================
function ResendDomainsPanel({ resendConfigured }) {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorState, setErrorState] = useState(null); // {kind, message}
  const [newDomain, setNewDomain] = useState("");
  const [creating, setCreating] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    setLoading(true);
    setErrorState(null);
    try {
      const r = await api.get("/admin/notifications/resend/domains");
      setDomains(r.data?.data || []);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.error === "send_only_key") {
        setErrorState({ kind: "send_only_key", message: detail.message });
      } else if (detail?.message) {
        setErrorState({ kind: "api", message: detail.message });
      } else {
        setErrorState({ kind: "unknown", message: "Failed to load domains" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (resendConfigured) load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [resendConfigured]);

  const createDomain = async () => {
    const name = newDomain.trim().toLowerCase();
    if (!name || !name.includes(".")) { toast.error("Enter a valid domain like mail.yourbrand.com"); return; }
    setCreating(true);
    try {
      await api.post("/admin/notifications/resend/domains", { name, region: "us-east-1" });
      toast.success(`${name} added — copy the DNS records below into your DNS provider`);
      setNewDomain("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Couldn't create domain");
    } finally {
      setCreating(false);
    }
  };

  const verifyDomain = async (id) => {
    try {
      await api.post(`/admin/notifications/resend/domains/${id}/verify`);
      toast.success("Verification triggered — refreshing status…");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Verification failed");
    }
  };

  const deleteDomain = async (id, name) => {
    if (!window.confirm(`Delete domain ${name}? You'll need to re-add it to send from this domain again.`)) return;
    try {
      await api.delete(`/admin/notifications/resend/domains/${id}`);
      toast.success(`${name} removed`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Delete failed");
    }
  };

  return (
    <section className="card-brutal" data-testid="resend-domains-panel">
      <div className="flex items-center gap-3 mb-1">
        <Globe size={20} className="text-[#FF4F00]" />
        <h3 className="font-display font-black text-xl">Resend Sending Domains</h3>
      </div>
      <p className="text-sm text-zinc-600 mb-5">
        Until you verify a domain you own, Resend only delivers email to the address that owns the API key. That means
        <strong> your leads won't actually receive their delivery emails in production.</strong> Verify a domain here to ship real mail to real inboxes.
      </p>

      {!resendConfigured && (
        <div className="border-2 border-black bg-yellow-100 p-4 mb-5 flex items-start gap-3" data-testid="resend-not-configured">
          <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong>Resend isn't configured.</strong> Set <code>RESEND_API_KEY</code> in <code>/app/backend/.env</code>, then restart the backend.
          </div>
        </div>
      )}

      {errorState?.kind === "send_only_key" && (
        <div className="border-2 border-black bg-orange-50 p-4 mb-5" data-testid="send-only-key-warning">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5 text-[#FF4F00]" />
            <div className="text-sm">
              <strong>Your Resend API key is send-only.</strong> Domain management requires a full-access key.
              <ol className="list-decimal ml-5 mt-2 space-y-1 text-zinc-700">
                <li>Go to <a className="underline font-bold" href="https://resend.com/api-keys" target="_blank" rel="noreferrer">resend.com/api-keys</a></li>
                <li>Click <strong>Create API Key</strong>, choose <strong>Full access</strong> permission</li>
                <li>Set <code>RESEND_MANAGEMENT_API_KEY=re_...</code> in <code>/app/backend/.env</code></li>
                <li>Run <code>sudo supervisorctl restart backend</code></li>
              </ol>
              <button onClick={load} className="btn-brutal-outline !px-3 !py-2 text-sm mt-3 inline-flex items-center gap-2" data-testid="retry-domains-btn">
                <RefreshCw size={14} /> Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {errorState?.kind === "api" && (
        <div className="border-2 border-black bg-red-50 p-3 mb-5 text-sm" data-testid="resend-api-error">
          <strong>Resend error:</strong> {errorState.message}
          <button onClick={load} className="btn-brutal-outline !px-3 !py-1 text-xs ml-3 inline-flex items-center gap-1">
            <RefreshCw size={12} /> Retry
          </button>
        </div>
      )}

      {!errorState && resendConfigured && (
        <>
          <div className="flex gap-2 mb-5">
            <input
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && createDomain()}
              className="input-brutal flex-1"
              placeholder="mail.yourbrand.com"
              data-testid="new-domain-input"
            />
            <button onClick={createDomain} disabled={creating} className="btn-brutal !px-4 !py-2 text-sm inline-flex items-center gap-2" data-testid="add-domain-btn">
              <Plus size={14} /> {creating ? "Adding…" : "Add domain"}
            </button>
          </div>

          {loading && <div className="text-sm text-zinc-500" data-testid="domains-loading">Loading domains…</div>}

          {!loading && domains.length === 0 && (
            <div className="border-2 border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500" data-testid="no-domains-yet">
              No domains yet. Add one above to start sending from your own brand.
            </div>
          )}

          <div className="space-y-3" data-testid="domains-list">
            {domains.map((d) => (
              <DomainRow
                key={d.id}
                domain={d}
                expanded={expandedId === d.id}
                onToggle={() => setExpandedId(expandedId === d.id ? null : d.id)}
                onVerify={() => verifyDomain(d.id)}
                onDelete={() => deleteDomain(d.id, d.name)}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function DomainRow({ domain, expanded, onToggle, onVerify, onDelete }) {
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const loadDetails = async () => {
    setLoadingDetails(true);
    try {
      const r = await api.get(`/admin/notifications/resend/domains/${domain.id}`);
      setDetails(r.data);
    } catch {
      // ignore: domain row keeps the summary view
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => { if (expanded && !details) loadDetails(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [expanded]);

  const status = (domain.status || "pending").toLowerCase();
  const verified = status === "verified";

  return (
    <div className="border-2 border-black" data-testid={`domain-row-${domain.name}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-zinc-50"
        data-testid={`domain-toggle-${domain.name}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <div className="font-mono font-bold truncate">{domain.name}</div>
          <StatusBadge status={status} />
          {domain.region && <span className="text-xs text-zinc-500">· {domain.region}</span>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!verified && (
            <span
              className="btn-brutal-outline !px-3 !py-1 text-xs inline-flex items-center gap-1"
              onClick={(e) => { e.stopPropagation(); onVerify(); }}
              data-testid={`verify-domain-${domain.name}`}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onVerify(); } }}
            >
              <RefreshCw size={12} /> Verify
            </span>
          )}
          <span
            className="btn-brutal-outline !px-2 !py-1 text-xs"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            data-testid={`delete-domain-${domain.name}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onDelete(); } }}
          >
            <Trash2 size={12} />
          </span>
        </div>
      </button>

      {expanded && (
        <div className="border-t-2 border-black p-4 bg-zinc-50" data-testid={`domain-records-${domain.name}`}>
          {loadingDetails && <div className="text-sm text-zinc-500">Loading DNS records…</div>}
          {!loadingDetails && details?.records?.length > 0 && (
            <>
              <div className="text-xs font-bold mb-2 label-cap">Add these records to your DNS provider:</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs font-mono border-2 border-black">
                  <thead className="bg-black text-white">
                    <tr>
                      <th className="px-2 py-1 text-left">Type</th>
                      <th className="px-2 py-1 text-left">Name</th>
                      <th className="px-2 py-1 text-left">Value</th>
                      <th className="px-2 py-1 text-left">TTL</th>
                      <th className="px-2 py-1 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.records.map((rec, i) => (
                      <tr key={i} className="border-t border-zinc-300">
                        <td className="px-2 py-2 align-top">{rec.type || rec.record}</td>
                        <td className="px-2 py-2 align-top break-all">
                          <CopyCell value={rec.name} />
                        </td>
                        <td className="px-2 py-2 align-top break-all max-w-md">
                          <CopyCell value={rec.value} />
                        </td>
                        <td className="px-2 py-2 align-top">{rec.ttl || "Auto"}</td>
                        <td className="px-2 py-2 align-top"><StatusBadge status={(rec.status || "pending").toLowerCase()} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-xs text-zinc-600 mt-3">
                After adding the records, click <strong>Verify</strong>. DNS propagation can take 5-30 minutes.
                Once status is <strong>verified</strong>, update <code>SENDER_EMAIL</code> in <code>/app/backend/.env</code> to use this domain (e.g. <code>hello@{domain.name}</code>).
              </div>
            </>
          )}
          {!loadingDetails && (!details?.records || details.records.length === 0) && (
            <div className="text-sm text-zinc-500">No DNS records returned by Resend yet — try the Verify button.</div>
          )}
        </div>
      )}
    </div>
  );
}

function CopyCell({ value }) {
  const copy = () => { navigator.clipboard.writeText(value || ""); toast.success("Copied"); };
  return (
    <span className="inline-flex items-center gap-2">
      <span>{value}</span>
      <button onClick={copy} className="text-zinc-400 hover:text-black" title="Copy">
        <Copy size={12} />
      </button>
    </span>
  );
}

function StatusBadge({ status }) {
  const styles = {
    verified: "bg-green-200 text-green-900 border-green-900",
    pending: "bg-yellow-200 text-yellow-900 border-yellow-900",
    not_started: "bg-zinc-200 text-zinc-900 border-zinc-900",
    failed: "bg-red-200 text-red-900 border-red-900",
    temporary_failure: "bg-orange-200 text-orange-900 border-orange-900",
  };
  const cls = styles[status] || styles.pending;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${cls}`}>
      {status.replace(/_/g, " ")}
    </span>
  );
}
